import { HttpClient, HttpContext, HttpHeaders } from '@angular/common/http'
import { Injectable } from '@angular/core'
// eslint-disable-next-line @nrwl/nx/enforce-module-boundaries
import {
  HeliosCommandTrace,
  RootState,
  clearCommands,
  clearCommandsByAccountId,
  selectActiveSession,
} from '@helios/state-management'
import { HANDLE_ERROR_AND_THROW } from '@helios/utils'
import { Store } from '@ngrx/store'
import { ISODateFormat, formatDate } from '@northfork/basics'
import { Observable } from 'rxjs'
import { map, take, tap } from 'rxjs/operators'
import {
  Command,
  CreateRestrictedActionRequest,
  RestrictedActionRequestTypes,
  RunType,
} from '../../models/command.model'

export const COMMANDS_URL = `helios-commands`
export const RUN_SESSION_URL = `${COMMANDS_URL}/run/session`
export const RUN_ACCOUNT_URL = `${COMMANDS_URL}/run/accounts`
export const RUN_RESTRICTED_ACTION = `${COMMANDS_URL}/restrictedAction/create`

export interface SessionRunRequestBody {
  notes?: string
  priority: number
  runEtls: boolean
  runType: RunType
  accountsToRun?: number[]
}

export interface AccountRunRequestBody {
  notes?: string
  priority: number
  reconDate: string
  runEtls: boolean
  runType: RunType
}

export interface AccountRunResponse {
  jobIds?: number[]
  failed?: CreateApprovalResponse[]
  invalidRequests?: CreateApprovalResponse[]
}

export interface CreateApprovalResponse {
  requestId?: number
  reconDate: string
  accountIds: number[]
  message: string
}

export interface RunFilteredAccountsPayload {
  accountIds: number[]
  reconDates: string[]
  priority: number
  runEtls: boolean
  runType: 'CUSTODY' | 'TRADING'
  repredict: boolean
}

export interface ForceReconTimestampsRequest {
  accountIds: number[]
  reconDates: string[]
  comment?: string
}

@Injectable({
  providedIn: 'root',
})
export class CommandService {
  /**
   * Used for running the active session
   *
   * @internal
   */
  activeSession$ = this.store.select(selectActiveSession)
  private readonly COMMAND_TIMEOUT = 1000 * 30

  constructor(
    private readonly http: HttpClient,
    private readonly store: Store<RootState>,
  ) {}

  generateTemporaryHeaders(panel: string, commandType: string) {
    return {
      'helios-internal-cmd-panel': panel,
      'helios-internal-cmd-action': commandType,
    }
  }

  formatTrace(trace: HeliosCommandTrace): HeliosCommandTrace {
    let previousTime: Date | undefined
    trace.timestampsFormatted = trace.timestamps.map(ts => {
      const keys = Object.keys(ts)
      if (keys.length === 0) {
        return {
          stage: '',
          timeSincePreviousInMs: 0,
        }
      }

      let elapsed = 0
      const currentTimestamp = new Date(ts[keys[0]])

      if (previousTime) {
        elapsed = currentTimestamp.getTime() - previousTime.getTime()
      }

      previousTime = currentTimestamp

      return {
        stage: keys[0],
        timeSincePreviousInMs: elapsed,
      }
    })

    trace.totalTimeInSeconds =
      trace.timestampsFormatted.reduce(
        (total, ts) => total + ts.timeSincePreviousInMs,
        0,
      ) / 1000

    return trace
  }

  /**
   * Gets the full list of recorded commands for a session.
   *
   * @param sessionId
   * @returns
   */
  getCommands(sessionId: string): Observable<Command[]> {
    const headers = new HttpHeaders({ 'helios-session-id': sessionId })
    return this.http.get<Command[]>(COMMANDS_URL, { headers: headers }).pipe(
      map(commands => {
        commands.sort((a, b) => {
          const aTs = new Date(a.createdTs)
          const bTs = new Date(b.createdTs)
          return bTs.getTime() - aTs.getTime()
        })

        return commands
      }),
    )
  }

  /**
   * Run account given a session.
   *
   * @param sessionId
   * @param runEtls Boolean to trigger an etl run or not
   * @param runType custody or trading, defaults to custody
   */
  runAccountBySession(
    sessionId: string,
    runEtls: boolean,
    runType: 'CUSTODY' | 'TRADING' = 'CUSTODY',
  ): Observable<void> {
    const headers = new HttpHeaders({ 'helios-session-id': sessionId })
    return this.http
      .post<void>(
        RUN_SESSION_URL,
        {
          priority: 0,
          runType,
          runEtls,
        },
        { headers: headers },
      )
      .pipe(tap(() => this.store.dispatch(clearCommands())))
  }

  /**
   * Runs accounts and returns an array of the successfully created job ids.
   * If the active session is among the requested accounts that session will be ran.
   *
   * @param accountIds
   * @param runEtls
   * @param reconDate
   * @param runType
   * @param priority
   */
  async run(
    accountIds: number[],
    runEtls: boolean,
    reconDate: Date,
    runType: RunType = 'CUSTODY',
    priority = 1,
  ): Promise<number[]> {
    const activeSession = await this.activeSession$.pipe(take(1)).toPromise()

    let accountsInSession: number[] //Loaded accounts + accounts with commands
    if (activeSession == null) accountsInSession = []
    else
      accountsInSession = [
        ...activeSession.loadedAccountIds,
        ...(activeSession.accountCommands?.map(ac => ac.accountId) ?? []),
      ]

    const jobIds: number[] = []
    const accountsToRunInSession: number[] = []
    for (const id of accountIds) {
      if (accountsInSession.includes(id)) {
        accountsToRunInSession.push(id)
      } else {
        jobIds.push(
          ...(await this.runAccount(
            id,
            runEtls,
            reconDate,
            runType,
            priority,
          ).toPromise()),
        )
      }
    }

    if (activeSession == null || accountsToRunInSession.length === 0)
      return jobIds

    jobIds.push(
      ...(await this.runSession(
        activeSession.id,
        runEtls,
        runType,
        priority,
        accountsToRunInSession,
      ).toPromise()),
    )

    return jobIds
  }

  /**
   * Runs job for the current active session. Returns the job id if the job was successfully submitted.
   * The backend runs the session for the session's recon date.
   *
   * @param sessionId
   * @param runEtls
   * @param runType
   * @param priority
   * @param accountsToRun
   */
  runSession(
    sessionId: string,
    runEtls: boolean,
    runType: RunType,
    priority: number,
    accountsToRun?: number[],
  ): Observable<number[]> {
    const body: SessionRunRequestBody = {
      runEtls,
      runType,
      priority,
      accountsToRun,
    }

    const headers = new HttpHeaders()
    headers.set('helios-session-id', sessionId)

    return this.http
      .post<AccountRunResponse>(RUN_SESSION_URL, body, { headers })
      .pipe(
        map(resp => resp.jobIds ?? []),
        tap(() => this.dispatchClearCommands(accountsToRun)),
      )
  }

  /**
   * Runs job for the current active session. Returns the job id if the job was successfully submitted.
   *
   * @param accountId
   * @param runEtls
   * @param reconDate
   * @param runType
   * @param priority - job priority level
   */
  runAccount(
    accountId: number,
    runEtls: boolean,
    reconDate: Date,
    runType: RunType,
    priority: number,
  ): Observable<number[]> {
    if (!reconDate) throw new Error('reconDate is required')

    const body: AccountRunRequestBody = {
      runEtls,
      runType,
      reconDate: formatDate(reconDate, ISODateFormat) as string,
      priority,
    }

    return this.http
      .post<AccountRunResponse>(`${RUN_ACCOUNT_URL}/${accountId}`, body)
      .pipe(
        map(resp => resp.jobIds ?? []),
        tap(() => this.store.dispatch(clearCommandsByAccountId({ accountId }))),
      )
  }

  runFilteredAccounts(
    payload: RunFilteredAccountsPayload,
  ): Observable<AccountRunResponse> {
    const options = {
      context: new HttpContext().set(HANDLE_ERROR_AND_THROW, true),
    }
    return this.http.post<AccountRunResponse>(
      `${RUN_ACCOUNT_URL}`,
      payload,
      options,
    )
  }

  runForceReconTS(
    payload: CreateRestrictedActionRequest,
  ): Observable<AccountRunResponse> {
    const options = {
      context: new HttpContext().set(HANDLE_ERROR_AND_THROW, true),
    }
    return this.http.post<AccountRunResponse>(
      `${RUN_RESTRICTED_ACTION}`,
      payload,
      options,
    )
  }

  runRestrictedAction(
    payload: CreateRestrictedActionRequest,
  ): Observable<AccountRunResponse> {
    const options = {
      context: new HttpContext().set(HANDLE_ERROR_AND_THROW, true),
    }
    return this.http.post<AccountRunResponse>(
      `${RUN_RESTRICTED_ACTION}`,
      payload,
      options,
    )
  }

  buildRestrictedActionRequest(
    commands: Command[],
    accountIds: number[],
    reconDateString: string,
    sessionId: string,
    runEtls: boolean,
    runType: RunType,
    totalCommands: number,
  ): CreateRestrictedActionRequest {
    const breakIds: string[] = commands.reduce<string[]>((acc, command) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const data = command.command?.commandData as any
      return acc.concat(data?.restrictedBreaks ?? [])
    }, [])

    return {
      actionType: RestrictedActionRequestTypes.RESTRICTED_BREAK,
      accountIds,
      reconDates: [reconDateString],
      comment: 'Restricted Break',
      metadata: {
        runEtls,
        runType,
        sessionId,
        breakIds,
        totalCommands,
      },
    }
  }

  checkForDuplicateCreateRequest(accountId: number): boolean {
    let result = false
    this.activeSession$.pipe(take(1)).subscribe(session => {
      const requestIds = session?.auxiliaryInfo?.REQUEST_IDS ?? []
      result = requestIds.some(r => r.accountId === accountId)
    })
    return result
  }

  buildRestrictedActionConfirmationMessage(reviewCommandCount: number): string {
    return (
      `There are ${reviewCommandCount} restricted commands in this submission that require approval.\n` +
      `All commands in this submission will be held until approval is granted.\n` +
      `All commands in this session will be discarded if approval is denied.`
    )
  }

  private dispatchClearCommands(accountsToRun?: number[]) {
    if (accountsToRun) {
      accountsToRun.forEach(accountId => {
        this.store.dispatch(
          clearCommandsByAccountId({
            accountId,
          }),
        )
      })
    } else {
      this.store.dispatch(clearCommands())
    }
  }
}
